import React, { useState } from "react";

import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import SortableChipList from "@/src/components/form-fields/SortableChipList";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { moveItem } from "@/src/utils/sort";

import type { ChangeEvent, FormEvent, JSX } from "react";

interface PlaceholderInputSearchProps {
  enabled: boolean;
  placeholdersList: string[];
  onAddPlaceholders: (placeholders: string[]) => void;
}

const DRAG_TYPE = "search-placeholder-chip";

function PlaceholderInputSearch({
  enabled,
  onAddPlaceholders,
  placeholdersList,
}: PlaceholderInputSearchProps): JSX.Element {
  const { t } = useTranslation("editor-placeholdersearch");
  const [placeholderInput, setPlaceholderInput] = useState("");
  const placeholderList = Array.from(new Set(placeholdersList));

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const placeholder = placeholderInput.trim();
    if (placeholder.length !== 0) {
      const otherPlaceholderList = placeholderList.filter(
        (item) => item !== placeholder,
      );
      onAddPlaceholders([...otherPlaceholderList, placeholder]);
      setPlaceholderInput("");
    }
  };

  const handleReorder = (fromIndex: number, toIndex: number): void => {
    onAddPlaceholders(moveItem(placeholderList, fromIndex, toIndex));
  };

  const handleRemove = (index: number): void => {
    onAddPlaceholders(
      placeholderList.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <SortableChipList
        disabled={!enabled}
        dragType={DRAG_TYPE}
        onRemove={handleRemove}
        onReorder={handleReorder}
        removeLabel={t("remove")}
        roleDescription={t("sortableItem")}
        items={placeholderList.map((placeholder) => ({
          id: placeholder,
          label: placeholder,
        }))}
      />
      <form
        className="flex max-w-[560px] gap-2"
        data-testid="PlaceholderInputSearch-form"
        onSubmit={handleSubmit}
      >
        <Input
          className="bg-card"
          data-testid="PlaceholderInputSearch-input"
          disabled={!enabled}
          placeholder={t("addPlaceholder")}
          value={placeholderInput}
          onChange={(event: ChangeEvent<HTMLInputElement>): void =>
            setPlaceholderInput(event.target.value)
          }
        />
        <Button
          data-testid="PlaceholderInputSearch-submit-button"
          disabled={!enabled || !placeholderInput.trim()}
          type="submit"
          variant="outline"
        >
          <Plus />
          {t("add")}
        </Button>
      </form>
    </div>
  );
}

export default PlaceholderInputSearch;
